import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router'

import { ApiError } from '../api/client'
import { endpoints, type BusinessType, type Timezone } from '../api/endpoints'
import { useSession } from '../auth/context'
import { PasswordField } from '../components/PasswordField'
import { LegalLinks } from './LegalPages'
import { errorMessage, t } from '../strings'

function useSubmit() {
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  async function run(action: () => Promise<void>) {
    setBusy(true)
    setError(null)
    try {
      await action()
    } catch (e) {
      setError(errorMessage(e instanceof ApiError ? e.code : 'unknown'))
    } finally {
      setBusy(false)
    }
  }
  return { error, busy, run }
}

function AuthShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <main className="auth">
      <div className="auth-header">
        <span className="wordmark">{t.appName}</span>
      </div>
      <section className="card">
        <h1>{title}</h1>
        {children}
      </section>
      <LegalLinks />
    </main>
  )
}

export function LoginPage() {
  const session = useSession()
  const navigate = useNavigate()
  const { error, busy, run } = useSubmit()
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')

  function submit(e: FormEvent) {
    e.preventDefault()
    void run(async () => {
      session.signedIn(await endpoints.login(phone, password))
      await navigate('/')
    })
  }

  return (
    <AuthShell title={t.loginTitle}>
      <form onSubmit={submit}>
        <label>
          {t.phone}
          <input inputMode="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required />
        </label>
        <PasswordField label={t.password} autoComplete="current-password" value={password} onChange={setPassword} />
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <button type="submit" disabled={busy}>
          {t.loginButton}
        </button>
      </form>
      <Link to="/daftar">{t.noAccount}</Link>
    </AuthShell>
  )
}

export function SignupPage() {
  const session = useSession()
  const navigate = useNavigate()
  const { error, busy, run } = useSubmit()
  const [f, setF] = useState({
    invite_code: '',
    phone: '',
    password: '',
    name: '',
    business_name: '',
    outlet_name: '',
    outlet_code: '',
    timezone: 'Asia/Jakarta' as Timezone,
    business_type: 'services' as BusinessType,
  })
  const [confirm, setConfirm] = useState('')
  const [confirmTouched, setConfirmTouched] = useState(false)
  const mismatch = confirmTouched && confirm !== f.password
  const field = (k: keyof typeof f) => ({
    value: f[k],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value }),
    required: true,
  })

  function submit(e: FormEvent) {
    e.preventDefault()
    setConfirmTouched(true)
    if (confirm !== f.password) return
    void run(async () => {
      session.signedIn(await endpoints.signup({ ...f, outlet_code: f.outlet_code.toUpperCase() }))
      await navigate('/')
    })
  }

  return (
    <AuthShell title={t.signupTitle}>
      <form onSubmit={submit}>
        <label>
          {t.inviteCode}
          <input autoCapitalize="characters" {...field('invite_code')} />
        </label>
        <label>
          {t.ownerName}
          <input autoComplete="name" {...field('name')} />
        </label>
        <label>
          {t.phone}
          <input inputMode="tel" autoComplete="tel" {...field('phone')} />
        </label>
        <PasswordField
          label={t.newPasswordLabel}
          autoComplete="new-password"
          minLength={8}
          value={f.password}
          onChange={(v) => setF({ ...f, password: v })}
        />
        <PasswordField
          label={t.confirmPassword}
          autoComplete="new-password"
          value={confirm}
          onChange={(v) => {
            setConfirm(v)
            if (v.length >= f.password.length) setConfirmTouched(true)
          }}
          error={mismatch ? t.passwordMismatch : null}
        />
        <label>
          {t.businessName}
          <input {...field('business_name')} />
        </label>
        <label>
          {t.outletName}
          <input {...field('outlet_name')} />
        </label>
        <label>
          {t.outletCode}
          <input autoCapitalize="characters" pattern="[A-Za-z0-9]{3,8}" maxLength={8} {...field('outlet_code')} />
        </label>
        <fieldset className="choice">
          <legend>{t.businessType}</legend>
          {(
            [
              ['services', t.businessServices, t.businessServicesHint],
              ['food', t.businessFood, t.businessFoodHint],
            ] as const
          ).map(([value, label, hint]) => (
            <label key={value} className={f.business_type === value ? 'choice-option on' : 'choice-option'}>
              <input
                type="radio"
                name="business_type"
                value={value}
                checked={f.business_type === value}
                onChange={() => setF({ ...f, business_type: value })}
              />
              <span>
                <strong>{label}</strong>
                <span className="muted small">{hint}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <label>
          {t.timezone}
          <select {...field('timezone')}>
            <option value="Asia/Jakarta">WIB (Jakarta)</option>
            <option value="Asia/Makassar">WITA (Makassar)</option>
            <option value="Asia/Jayapura">WIT (Jayapura)</option>
          </select>
        </label>
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <button type="submit" disabled={busy}>
          {t.signupButton}
        </button>
      </form>
      <Link to="/masuk">{t.haveAccount}</Link>
    </AuthShell>
  )
}

export function ChangePasswordPage() {
  const session = useSession()
  const { error, busy, run } = useSubmit()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [confirmTouched, setConfirmTouched] = useState(false)
  const mismatch = confirmTouched && confirm !== next

  function submit(e: FormEvent) {
    e.preventDefault()
    setConfirmTouched(true)
    if (confirm !== next) return
    void run(async () => {
      const out = await endpoints.changePassword(current, next)
      session.signedIn({ access_token: out.access_token, expires_in: 900, password_must_change: false })
    })
  }

  return (
    <AuthShell title={t.changePasswordTitle}>
      <p>{t.changePasswordBody}</p>
      <form onSubmit={submit}>
        <PasswordField label={t.currentPassword} autoComplete="current-password" value={current} onChange={setCurrent} />
        <PasswordField label={t.newPassword} autoComplete="new-password" minLength={8} value={next} onChange={setNext} />
        <PasswordField
          label={t.confirmPassword}
          autoComplete="new-password"
          value={confirm}
          onChange={(v) => {
            setConfirm(v)
            if (v.length >= next.length) setConfirmTouched(true)
          }}
          error={mismatch ? t.passwordMismatch : null}
        />
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <button type="submit" disabled={busy}>
          {t.save}
        </button>
      </form>
    </AuthShell>
  )
}
